import type { RouteLocationNormalizedLoaded } from 'vue-router'
export function escapeRegexp(target: string): string {
  if (import.meta.client) {
    return RegExp.escape(target)
  } else {
    return target.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }
}

export function getFullPath(path: string) {
  const config = useRuntimeConfig()
  if (path.startsWith('/')) {
    path = path.substring(1)
  }
  return config.public.serviceBaseUrl + config.app.baseURL + path
}

export function getQueryMap(url: string): { [key: string]: string[] } {
  const vars: { [key: string]: string[] } = {}
  if (!url) {
    return vars
  }
  if (url.startsWith('?')) {
    url = url.slice(1)
  }
  const query: string[] = url.split('&')
  for (let i = 0; i < query.length; i++) {
    const array: string[] = query[i].split('=')
    if (array[0]) {
      const field = decodeURIComponent(array[0])
      if (!vars[field]) {
        vars[field] = []
      }
      vars[field].push(decodeURIComponent(array[1]))
    }
  }
  return vars
}

export function toQueryString(map: { [key: string]: string[] }): string {
  let q = ''
  Object.keys(map).forEach(key => {
    const val = map[key]
    if (Array.isArray(val)) {
      ;(<any[]>val).forEach(a => {
        if (q.length > 0) q += '&'
        q += encodeURIComponent(key) + '=' + encodeURIComponent(a)
      })
    } else {
      if (q.length > 0) q += '&'
      q += encodeURIComponent(key) + '=' + encodeURIComponent(val)
    }
  })
  return '?' + q
}

export function getPath(): string {
  return window.location.pathname
}

type Pattern = {
  from: RegExp
  to: RegExp
}

const URL_PATTERN = /(https?:\/\/[a-zA-Z0-9.\-_@:/~?%&;=+#',()*!]+)/g

export function containsValidUrl(url: string): boolean {
  return !!url.match(URL_PATTERN)?.length
}

export function getQueryMapFromRoute(route: RouteLocationNormalizedLoaded): {
  [key: string]: string[]
} {
  const vars: { [key: string]: string[] } = {}
  Object.keys(route.query).forEach(key => {
    if (Array.isArray(route.query[key])) {
      vars[key] = route.query[key] as string[]
    } else {
      vars[key] = [route.query[key] as string]
    }
  })
  return vars
}

export function isEnPath(path: string): boolean {
  const npath = normalizePath(path)
  return npath === '/en' || npath.startsWith('/en/')
}

export function getPathLang(path: string): string {
  return isEnPath(path) ? 'en' : 'ja'
}

export function getNonLangPath(path: string): string {
  const npath = normalizePath(path)
  if (npath === '/en') {
    return '/'
  }
  if (npath.startsWith('/en/') || npath.startsWith('/en?') || npath.startsWith('/en#')) {
    return npath.substring(3)
  }
  return npath
}

export function getLangPath(isEn: boolean, path: string): string {
  if (!path) return path
  const npath = getNonLangPath(normalizePath(path))
  if (isEn) {
    return normalizePath('/en' + npath)
  } else {
    return npath
  }
}

export function normalizePath(path: string): string {
  if (!path || typeof path !== 'string') {
    return '/'
  }
  if (path.endsWith('/')) {
    path = path.substring(0, path.length - 1)
  }
  if (!path.length) {
    path = '/'
  }
  return path
}

export function linkObjectToLink(linkObject: any): string {
  if (linkObject instanceof Object) {
    const link: object = linkObject
    if ('nolink' in link || 'exlink' in link) {
      return linkObject
    } else if ('to' in link) {
      if (link.to instanceof Object) {
        const to: any = link.to
        return linkParamToLink(to)
      } else {
        return link.to
      }
    }
  }
  return ''
}

export function linkParamToLink(linkParam: any, otherLink: string = undefined): string {
  if (!linkParam && !otherLink) {
    return ''
  }
  if (typeof linkParam === 'string') {
    return linkParam
  }

  const config = useRuntimeConfig()
  try {
    if (linkParam && 'name' in linkParam && 'params' in linkParam && linkParam.params instanceof Object) {
      const params: any = linkParam.params
      switch (linkParam.name) {
        case 'database':
          return `/${linkParam.name}/${params.dbId}`
        case 'gallery':
          return `/${linkParam.name}/${params.curId}`
        case 'csearch':
          return `/${linkParam.name}/${params.csId}`
        case 'organization':
          return `/${linkParam.name}/${params.orgId}`
        case 'project':
          return `/${linkParam.name}/${params.orgId}`
        case 'workspace':
          return `/${linkParam.name}/${params.wsId}`
        case 'item':
          return `/${linkParam.name}/${params.itemId}`
      }
    } else if (otherLink) {
      const regex = new RegExp('^https://(.*)' + escapeRegexp(config.public.baseDomain))
      if (regex.test(otherLink)) {
        // 内部リンクが外部リンクとして扱われないように書き換え
        return otherLink.replace(regex, '')
      } else {
        return otherLink
      }
    } else {
      return linkParam
    }
  } catch (error) {
    console.log(error)
  }
  return ''
}

export function cnvParamPath(path: string, params: { [key: string]: string }): string {
  if (Object.keys(params).length === 0) {
    // paramの指定がない場合はそのままpathを返す
    return path
  }

  // paramの指定がある場合はparamをパスに追加
  // すでにparamがあったのであれば&でparamを追加
  // paramがない場合は新規でparamを追加
  const delimiter = path.includes('?') ? '&' : '?'

  const param = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')

  return `${path}${delimiter}${param}`
}
